import axios from "axios";
import { Button, DataGrid } from "devextreme-react";
import {
  Column,
  ColumnChooser,
  Export,
  FilterPanel,
  FilterRow,
  GroupPanel,
  HeaderFilter,
  Item,
  LoadPanel,
  Pager,
  Paging,
  SearchPanel,
  StateStoring,
  Toolbar,
} from "devextreme-react/data-grid";
import { exportDataGrid } from "devextreme/excel_exporter";
import { exportDataGrid as exportDataGridToPDF } from "devextreme/pdf_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import jsPDF from "jspdf";
import "jspdf-autotable";
import React, { useEffect, useRef, useState } from "react";

import { malgun } from "./malgun";

const pageSizes = [10, 20, 50, 100];

export default function MedicalDeviceDataGrid({
  endpoint,
  keyExpr,
  columns,
  storageKey,
  exportTitle,
}) {
  const [dataSource, setDataSource] = useState(null);
  const [loadError, setLoadError] = useState("");
  const dataGridRef = useRef(null);

  useEffect(() => {
    let active = true;

    axios
      .get(`${process.env.REACT_APP_BECCO_API_URL}${endpoint}`)
      .then((result) => {
        if (active) setDataSource(result.data);
      })
      .catch(() => {
        if (active) {
          setLoadError("데이터를 불러오지 못했습니다. 잠시 후 다시 시도해주세요.");
          setDataSource([]);
        }
      });

    return () => {
      active = false;
    };
  }, [endpoint]);

  const onExporting = (e) => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet(exportTitle);

    exportDataGrid({
      component: e.component,
      worksheet,
      autoFilterEnabled: true,
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(
          new Blob([buffer], { type: "application/octet-stream" }),
          `${exportTitle}.xlsx`
        );
      });
    });

    e.cancel = true;
  };

  const exportPdf = () => {
    const doc = new jsPDF("l", "mm", [297, 210]);
    doc.addFileToVFS("malgun.ttf", malgun);
    doc.addFont("malgun.ttf", "malgun", "normal");
    doc.setFont("malgun");

    exportDataGridToPDF({
      jsPDFDocument: doc,
      component: dataGridRef.current.instance,
      autoTableOptions: { styles: { font: "malgun" } },
    }).then(() => doc.save(`${exportTitle}.pdf`));
  };

  if (dataSource === null) {
    return <div style={{ padding: "24px" }}>데이터를 불러오는 중입니다.</div>;
  }

  return (
    <>
      {loadError && (
        <div role="alert" style={{ color: "#c62828", padding: "12px 0" }}>
          {loadError}
        </div>
      )}
      <DataGrid
        ref={dataGridRef}
        dataSource={dataSource}
        keyExpr={keyExpr}
        rowAlternationEnabled={true}
        showBorders={true}
        showColumnLines={true}
        allowColumnResizing={true}
        columnAutoWidth={true}
        focusedRowEnabled={true}
        onExporting={onExporting}
      >
        <ColumnChooser enabled={true} />
        <LoadPanel enabled={true} />
        <FilterRow visible={true} />
        <HeaderFilter visible={true} />
        <FilterPanel visible={true} />
        <GroupPanel visible={true} />
        <SearchPanel visible={true} highlightCaseSensitive={false} />
        <StateStoring enabled={true} type="localStorage" storageKey={storageKey} />

        {columns.map((column) => (
          <Column key={column.dataField || column.caption} {...column} />
        ))}

        <Toolbar>
          <Item location="before" name="groupPanel" />
          <Item location="after">
            <Button icon="exportpdf" onClick={exportPdf} />
          </Item>
          <Item name="exportButton" />
          <Item name="columnChooserButton" />
          <Item name="searchPanel" />
        </Toolbar>

        <Export enabled={true} />
        <Pager
          visible={true}
          allowedPageSizes={pageSizes}
          showPageSizeSelector={true}
          showInfo={true}
        />
        <Paging enabled={true} defaultPageSize={20} />
      </DataGrid>
    </>
  );
}
